/**
 * feat-270 — the curriculum approval queue at `/quiz-approvals`.
 *
 * Design authority: `design/approval-queue.html` (11 states) and
 * `design/NOTES.md`. API contract: `decisions/ADR-feat-270-api-contract.md` r3 —
 * §3.2 alone drives this whole page including every diff, so there is no
 * per-item fetch, no `curriculum_change_history` call and no name lookup.
 *
 * A ROUTE rather than a slide-over (NOTES §7): both existing review surfaces are
 * sibling routes off the same header strip, the approver arrives to do one job
 * and leaves, a two-column diff needs full width that a 448px slide-over cannot
 * give, and it is linkable — which matters concretely, because the link gets
 * sent to the person who approves.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE RULES THAT ARE LOAD-BEARING AND EASY TO UNDO.
 *
 * 1. **The queue never auto-refreshes under the reader** (NOTES §6a). A list that
 *    reorders or gains rows while someone is reading it is the same hazard the
 *    fingerprint exists to stop, one layer up. The fingerprint is precisely what
 *    makes staleness safe to tolerate: the screen may be out of date because the
 *    write cannot be. Refresh is a button the reader presses.
 *
 * 2. **A 409 redraws from its own response body** (AT-27bb) — no follow-up fetch.
 *    Only the 409 carries `is_new_since_review`; a re-read has it `null`, so
 *    refetching would silently discard the one field that makes re-review
 *    possible.
 *
 * 3. **After approving, the approved lessons leave the list and the conflicted
 *    ones are replaced in place.** The rest of the list is left exactly as the
 *    reader had it — including which cards they had expanded.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import ScorecardNavigation from '../restaurant/ScorecardNavigation';
import { APPROVAL_QUEUE_COPY as C, changeCount } from './curriculum/approvalCopy';
import {
  EMPTY_SELECTION,
  clearSelection,
  requiresConfirm,
  selectAll,
  summarizeSelection,
  toggleLesson,
  type ApprovalSelection,
} from './curriculum/approvalSelection';
import { ApprovalLessonCard } from './curriculum/approval/ApprovalLessonCard';
import { ApproveAllDialog } from './curriculum/approval/ApproveAllDialog';
import { timeAgo } from './curriculum/relativeTime';
import { QUIZ_TERMS, termCount } from './quizTerminology';
import {
  actorDisplayName,
  approveLessons,
  getApprovalQueue,
  getApprovalSummary,
  type ApprovalLessonDetail,
  type ApprovalQueue,
  type ApprovalSummary,
} from '../../services/curriculumApprovalService';
import { revertCurriculumChange } from '../../services/quizContentAdminService';

type LoadState = 'loading' | 'ready' | 'error';

/** The persistent result strip (state H / I). NEVER a toast. */
interface ResultStrip {
  approvedTitles: string[];
  approvedAt: string;
  conflictedTitles: string[];
  conflictActor: string | null;
  failedTitles: string[];
}

function formatClock(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function formatStamp(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const d = new Date(t);
  return `${d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })} at ${formatClock(d)}`;
}

export const CurriculumApprovalQueue: React.FC = () => {
  const { selectedTenant, tenantName } = useTenant();

  const [state, setState] = useState<LoadState>('loading');
  const [queue, setQueue] = useState<ApprovalQueue | null>(null);
  /**
   * §3.1, fetched alongside §3.2 rather than instead of it.
   *
   * ADR-B §5 says "§3.2 alone drives the queue including every diff", and that is
   * true of the CARDS. It is not true of design's state C, whose whole argument
   * is that naming the last approval is what makes the zero bankable — and
   * `lessons_total` and `last_approval` live only on §3.1. Two calls in parallel,
   * and §3.1 is the cheap one (ADR-A evidence T6: ~1.5ms).
   */
  const [summaryData, setSummaryData] = useState<ApprovalSummary | null>(null);
  const [lessons, setLessons] = useState<ApprovalLessonDetail[]>([]);
  const [selection, setSelection] = useState<ApprovalSelection>(EMPTY_SELECTION);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<ResultStrip | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [approving, setApproving] = useState(false);
  /** Lessons whose card is drawn from a 409 body (state I's `attention` border). */
  const [conflicted, setConflicted] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    if (!selectedTenant) return;
    setState('loading');
    try {
      const [data, summaryResponse] = await Promise.all([
        getApprovalQueue(selectedTenant),
        getApprovalSummary(selectedTenant),
      ]);
      setQueue(data);
      setSummaryData(summaryResponse);
      setLessons(data.lessons);
      setSelection(EMPTY_SELECTION);
      setConflicted(new Set());
      setState('ready');
    } catch {
      // §10.10 / state D: this asserts IGNORANCE. It must never render as a zero
      // — a failed fetch drawn as an empty queue tells the reader everything is
      // approved, which is the one thing we cannot know here.
      setState('error');
    }
  }, [selectedTenant]);

  useEffect(() => {
    void load();
  }, [load]);

  const canApprove = queue?.can_approve === true;
  const allIds = useMemo(() => lessons.map((l) => l.lesson_id), [lessons]);

  // Counts recompute from local state, so approving updates them without a page
  // reload (AT-27) and without refetching under the reader (NOTES §6a).
  const pendingChanges = useMemo(
    () => lessons.reduce((sum, l) => sum + l.pending_item_count, 0),
    [lessons],
  );

  const summary = useMemo(
    () => summarizeSelection(selection, lessons),
    [selection, lessons],
  );

  const toggleExpanded = useCallback((lessonId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(lessonId)) next.delete(lessonId);
      else next.add(lessonId);
      return next;
    });
  }, []);

  const runApprove = useCallback(
    async (targets: ApprovalLessonDetail[]) => {
      if (!selectedTenant || targets.length === 0) return;
      setApproving(true);
      setConfirming(false);
      try {
        // AT-27 / AT-27c — ONE approve call per lesson. Partial success is the
        // ordinary outcome whenever anyone else is editing, not an edge case.
        const outcome = await approveLessons(
          selectedTenant,
          targets.map((l) => ({
            lesson_id: l.lesson_id,
            title: l.title,
            // The fingerprint the reader RENDERED — never one re-fetched just
            // before the click, which would defeat the guard by re-reading the
            // very edit the approver did not see.
            pending_fingerprint: l.pending_fingerprint,
          })),
        );

        const approvedIds = new Set(outcome.approved.map((o) => o.lessonId));
        const conflictById = new Map(
          outcome.conflicts.map((o) => [o.lessonId, o.conflict.lesson]),
        );

        setLessons((prev) =>
          prev
            // Approved lessons leave the list; nothing about them is pending now.
            .filter((l) => !approvedIds.has(l.lesson_id))
            // A conflicted lesson is REPLACED IN PLACE from the 409's own body
            // (AT-27bb). No follow-up fetch: only this response carries
            // `is_new_since_review`, and a re-read would have it null.
            .map((l) => conflictById.get(l.lesson_id) ?? l),
        );

        setConflicted(new Set(conflictById.keys()));
        // A conflicted card is worth reading, so open it.
        setExpanded((prev) => {
          const next = new Set(prev);
          for (const id of conflictById.keys()) next.add(id);
          return next;
        });
        setSelection(EMPTY_SELECTION);

        const firstConflict = outcome.conflicts[0]?.conflict.lesson;
        setResult({
          approvedTitles: outcome.approved.map((o) => o.title),
          approvedAt: formatClock(new Date()),
          conflictedTitles: outcome.conflicts.map((o) => o.title),
          conflictActor: firstConflict?.last_change_by
            ? actorDisplayName(firstConflict.last_change_by)
            : null,
          failedTitles: outcome.failed.map((o) => o.title),
        });
      } finally {
        setApproving(false);
      }
    },
    [selectedTenant],
  );

  const onBulkApprove = useCallback(() => {
    // AT-27d. The predicate is PROVENANCE — `requiresConfirm` reads the flag the
    // select-all control set, and nothing here compares a count to a total.
    if (requiresConfirm(selection)) {
      setConfirming(true);
      return;
    }
    void runApprove(summary.lessons);
  }, [selection, summary.lessons, runApprove]);

  const onRestore = useCallback(
    async (historyId: string) => {
      try {
        await revertCurriculumChange(historyId);
        await load();
      } catch {
        setState('error');
      }
    },
    [load],
  );

  // ── state G — no tenant selected ──────────────────────────────────────────
  if (!selectedTenant) {
    return (
      <Shell>
        <div className="p-4 bg-attention-surface border border-attention-line/50 rounded-2xl text-sm text-attention-on">
          {C.noTenant}
        </div>
      </Shell>
    );
  }

  // ── state B — loading ─────────────────────────────────────────────────────
  if (state === 'loading') {
    return (
      <Shell tenantName={tenantName}>
        {/* Select-all and Refresh are ABSENT, not disabled: there is nothing to
            select yet, and a disabled control implies a precondition the reader
            can meet. */}
        <div className="space-y-3" aria-busy="true" aria-live="polite" aria-label={C.loading}>
          <div className="h-4 w-40 bg-gray-100 rounded animate-pulse" />
          <div className="h-14 bg-gray-100 rounded-2xl animate-pulse" />
          <div className="h-14 bg-gray-100 rounded-2xl animate-pulse" />
          <div className="h-14 w-11/12 bg-gray-100 rounded-2xl animate-pulse" />
        </div>
      </Shell>
    );
  }

  // ── state D — error. Asserts IGNORANCE, never a zero. ─────────────────────
  if (state === 'error' || !queue) {
    return (
      <Shell tenantName={tenantName}>
        <div
          className="bg-danger-surface border border-danger-line/40 text-danger-text rounded-2xl px-5 py-4 flex items-center gap-4"
          role="alert"
        >
          <span className="text-2xl shrink-0" aria-hidden>
            ⚠
          </span>
          <div className="min-w-0">
            <div className="font-display font-semibold">{C.errorTitle}</div>
            {/* The sentence that does the work is the second one: nobody may read
                a failed fetch as an empty queue. */}
            <div className="text-sm mt-0.5">{C.errorBody}</div>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="bg-danger-text text-white font-display font-semibold rounded-full px-4 py-2 text-sm hover:brightness-110 shrink-0"
          >
            {C.errorRetry}
          </button>
        </div>
      </Shell>
    );
  }

  // ── AT-29 — the flag is off. Reached only by a pasted link, since the entry
  //    point is absent in the editor. NOT the empty state, which would be a lie.
  if (!queue.approval_required) {
    return (
      <Shell tenantName={tenantName}>
        <div className="bg-white border border-softBorder rounded-3xl shadow-sm p-8 text-center max-w-md mx-auto">
          <h2 className="font-display text-lg font-semibold">{C.notEnabledTitle}</h2>
          <p className="text-sm text-inkMuted mt-1.5 leading-relaxed">{C.notEnabledBody}</p>
          <Link
            to="/quiz-management"
            className="inline-block mt-4 bg-coral text-white font-display font-semibold rounded-full px-5 py-2 text-sm hover:brightness-95"
          >
            {C.emptyAction}
          </Link>
        </div>
      </Shell>
    );
  }

  const strip = result ? <ResultStripView result={result} /> : null;

  // ── state C — empty. A MEASUREMENT, and it must read as one. ──────────────
  if (lessons.length === 0) {
    return (
      <Shell tenantName={tenantName}>
        {strip}
        <div className="bg-white border border-softBorder rounded-3xl shadow-sm p-8 text-center max-w-md mx-auto">
          <div className="text-5xl mb-3" aria-hidden>
            ✅
          </div>
          <h2 className="font-display text-lg font-semibold">{C.emptyTitle}</h2>
          <p className="text-sm text-inkMuted mt-1.5 leading-relaxed">
            {C.emptyBody(summaryData?.lessons_total ?? 0, tenantName)}{' '}
            {/* Naming the last approval is what makes the zero bankable — an
                unqualified "everything is approved" is a claim the reader has no
                way to check. Absent on a tenant that has genuinely never
                approved, where there is no last approval to name. */}
            {summaryData?.last_approval && (
              <>
                {C.emptyLastApproval(
                  summaryData.last_approval.lesson_title,
                  actorDisplayName(summaryData.last_approval.approved_by),
                  formatStamp(summaryData.last_approval.approved_at),
                )}
              </>
            )}
          </p>
          <Link
            to="/quiz-management"
            className="inline-block mt-4 bg-coral text-white font-display font-semibold rounded-full px-5 py-2 text-sm hover:brightness-95"
          >
            {C.emptyAction}
          </Link>
        </div>
      </Shell>
    );
  }

  // ── state A / F — the queue ───────────────────────────────────────────────
  return (
    <Shell tenantName={tenantName} showConsequence>
      {strip}

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        {/* AT-28: no select-all for a reader who cannot approve. */}
        {canApprove && (
          <label className="flex items-center gap-2 text-sm font-semibold text-ink cursor-pointer">
            <input
              type="checkbox"
              className="w-3.5 h-3.5 accent-coral"
              checked={selection.ids.size === lessons.length && lessons.length > 0}
              onChange={(e) =>
                // The ONLY place `fromSelectAll` becomes true (AT-27d).
                setSelection(e.target.checked ? selectAll(allIds) : clearSelection())
              }
            />
            {C.selectAll(lessons.length)}
          </label>
        )}
        {canApprove && <span className="w-px h-5 bg-softBorder" aria-hidden />}
        <span className="text-sm text-inkMuted">
          <span className="font-semibold text-ink">
            {termCount(lessons.length, 'lesson')}
          </span>{' '}
          waiting · {changeCount(pendingChanges)}
        </span>
        <button
          type="button"
          onClick={() => void load()}
          className="ml-auto text-sm font-semibold rounded-full border border-softBorder text-ink px-3 py-1.5 hover:bg-white"
        >
          {C.refresh}
        </button>
      </div>

      {lessons.map((lesson) => (
        <ApprovalLessonCard
          key={lesson.lesson_id}
          lesson={lesson}
          canApprove={canApprove}
          selected={selection.ids.has(lesson.lesson_id)}
          onToggleSelected={() =>
            setSelection((prev) => toggleLesson(prev, lesson.lesson_id))
          }
          expanded={expanded.has(lesson.lesson_id)}
          onToggleExpanded={() => toggleExpanded(lesson.lesson_id)}
          conflicted={conflicted.has(lesson.lesson_id)}
          onApprove={() => void runApprove([lesson])}
          approving={approving}
          onRestore={onRestore}
        />
      ))}

      <div className="h-16" />

      {/*
        THE BULK BAR. AT-28 / NOTES §4: for a store manager it is still rendered,
        permanently, with ONE disabled pill in the primary slot — "a suppressed
        action's pill has to occupy the button's slot at the same geometry — that
        is what makes the absence of a control read as a decision rather than as a
        missing feature." Not eight dead checkboxes.
      */}
      {canApprove ? (
        selection.ids.size > 0 && (
          <div className="sticky bottom-0 border-t border-softBorder bg-coral-light px-6 py-2.5 flex items-center justify-between gap-2">
            <span className="text-xs font-bold text-coral">
              {C.bulkSelected(summary.lessonCount, summary.changeCount)}
            </span>
            <span className="flex gap-2 items-center">
              <button
                type="button"
                onClick={() => setSelection(clearSelection())}
                className="text-[11px] font-bold rounded-full px-2.5 py-1 border border-softBorder bg-white text-ink"
              >
                {C.clear}
              </button>
              <button
                type="button"
                disabled={approving}
                onClick={onBulkApprove}
                className="text-[11px] font-bold rounded-full px-3 py-1 bg-coral text-white hover:brightness-95 disabled:opacity-50"
              >
                {C.bulkApprove(summary.lessonCount)}
              </button>
            </span>
          </div>
        )
      ) : (
        <div className="sticky bottom-0 border-t border-softBorder bg-cream-dark px-6 py-2.5 flex items-center justify-between gap-3">
          <span id="approve-gate-note" className="text-xs text-inkMuted">
            {C.approveGateNote}
          </span>
          {/*
            `aria-describedby` is NOT optional here. `components.md` logs it as
            known debt on both shipped gated-capability rows: a disabled <button>
            is out of the tab order, so a keyboard or screen-reader user never
            lands on the control and never receives the explanation — the
            pattern's premise is self-explaining discoverability, and that premise
            is weakest for the users who most need it. Do not copy the debt
            forward a third time.
          */}
          <button
            type="button"
            disabled
            aria-describedby="approve-gate-note"
            className="text-[11px] font-bold rounded-full px-3 py-1 bg-white border border-softBorder text-inkMuted opacity-50 cursor-not-allowed"
          >
            {C.approve}
          </button>
        </div>
      )}

      {confirming && (
        <ApproveAllDialog
          summary={summary}
          onCancel={() => setConfirming(false)}
          onConfirm={() => void runApprove(summary.lessons)}
        />
      )}
    </Shell>
  );
};

/** Page chrome, shared by every state so the header never moves between them. */
const Shell: React.FC<{
  tenantName?: string;
  showConsequence?: boolean;
  children: React.ReactNode;
}> = ({ tenantName, showConsequence, children }) => (
  <div className="min-h-screen bg-cream">
    <ScorecardNavigation />
    <div className="max-w-6xl mx-auto p-4 md:p-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-display font-semibold text-ink mb-1">{C.title}</h1>
          {tenantName && (
            <p className="text-sm text-inkMuted max-w-2xl">
              {C.subtitlePrefix}
              <span className="font-semibold text-ink">{tenantName}</span>
              {C.subtitleSuffix}
              {showConsequence && <> {C.subtitleConsequence}</>}
            </p>
          )}
        </div>
        <Link
          to="/quiz-management"
          className="shrink-0 text-coral text-sm font-semibold hover:underline mt-2"
        >
          {C.backToEditor}
        </Link>
      </div>
      {children}
    </div>
  </div>
);

/**
 * The result strip (states H and I). PERSISTENT, never a toast — feat-199's
 * rule: "a toast is a caption with a timer; it vanishes, and the hazard is
 * precisely that she is not looking when it does."
 *
 * And it NAMES the lessons rather than saying "Saved ✓": a generic confirmation
 * makes a mis-click legible as *something happened*; naming them makes it
 * legible as *wrong*, and only the second is a control.
 *
 * AT-27c: a partial success carries BOTH halves, each naming its own lessons. A
 * single "Something went wrong" after approving 2 of 3 leaves the reader unable
 * to tell which two went live — and they went live.
 */
const ResultStripView: React.FC<{ result: ResultStrip }> = ({ result }) => (
  <>
    {result.approvedTitles.length > 0 && (
      <div
        className="mb-5 rounded-2xl border border-softBorder bg-success-light px-4 py-3 text-sm"
        role="status"
      >
        <div className="font-semibold text-success-on">
          {C.approvedStrip(result.approvedTitles.length, result.approvedAt)}
        </div>
        <div className="text-ink mt-1 text-[13px]">{result.approvedTitles.join(' · ')}</div>
        {/* Spec D8: there is no un-approve, and the strip says so rather than
            leaving the reader to look for an Undo that does not exist. */}
        <div className="text-inkMuted mt-1.5 text-xs">{C.noUndoNote}</div>
      </div>
    )}

    {result.conflictedTitles.length > 0 && (
      // NOT an error, and it must not be dressed as one — the guard WORKED.
      // `attention-*`, because this is a problem the reader can fix from this
      // panel (the feat-259 split), and the copy leads with what did NOT happen.
      <div
        className="mb-5 rounded-2xl bg-attention-surface text-attention-on border border-attention-line/50 px-4 py-3 text-sm"
        role="alert"
      >
        <div className="font-bold">{C.conflictStripTitle(result.conflictedTitles.length)}</div>
        <div className="mt-1 text-[13px] leading-snug">
          <span className="font-semibold">{result.conflictedTitles.join(' · ')}</span>
          {result.conflictActor && <> — {C.conflictStripBody(result.conflictActor)}</>}{' '}
          <strong>{C.conflictStripNothingApproved}</strong>
          {C.conflictStripRest}
        </div>
      </div>
    )}

    {result.failedTitles.length > 0 && (
      <div
        className="mb-5 rounded-2xl bg-danger-surface border border-danger-line/40 text-danger-text px-4 py-3 text-sm"
        role="alert"
      >
        <div className="font-semibold">{C.failedStripTitle(result.failedTitles.length)}</div>
        <div className="mt-1 text-[13px]">{result.failedTitles.join(' · ')}</div>
        <div className="mt-1 text-[13px]">{C.failedStripBody}</div>
      </div>
    )}
  </>
);

export default CurriculumApprovalQueue;
